/*
 * HistoryViewportController — the History tab's data plane. Owns the viewport
 * {startMs,endMs}, picks the resolution (raw vs bucket rung) for it, and keeps
 * a BOUNDED per-zoom-level cache fed by margin-prefetch and trimmed by
 * eviction — so a drag has runway past both edges, while memory stays flat
 * however much archive the user scrubs across (what's resident is bounded by
 * pixels × a few levels, never by history depth).
 *
 * This class is where "components never fetch" lives: the section instantiates
 * it, the chart element renders what it holds and emits gesture deltas; all IO
 * goes through the Backend Worker's historyQuery (which throws on failure —
 * so a failed fetch is retried, never cached as a false "confirmed empty").
 *
 * Stale-response safety: fetches are stamped with the level they were issued
 * for and merged into that level's entry only if it still exists; merges only
 * ever ADD buckets inside the level's own desired range. (Trimming keeps one
 * off-screen "sentinel" bucket beyond each keep edge — resident but outside
 * `covered` — so the polyline still enters the viewport from off-screen at
 * deep zoom instead of disconnecting at the edge.) A landing response
 * can therefore never move the viewport, rescale the axis (the chart scales
 * from visible buckets only), or blank out data already on screen.
 */
import type { BackendClient } from '../../bridge/backend-client';
import type { HistoryBucket } from '../../../core/bridge/connect-client';
import {
  MAX_VIEWPORT_SPAN_MS,
  MIN_VIEWPORT_SPAN_MS,
  nextCoarserRung,
  nextFinerRung,
  pickBucketSeconds,
  snapRangeToBuckets,
  zoomViewport,
  type TimelineViewport,
} from '../chart/timeline-types';

/** What the chart renders: the active level's buckets + what's CONFIRMED
 *  fetched (data or genuinely empty). Ranges outside `covered` are "not asked
 *  yet" — the chart paints them as loading, never as gap or zero. */
export interface TimelineData {
  buckets: HistoryBucket[];
  /** Bucket width in ms, or null at the raw-samples level. */
  bucketMs: number | null;
  /** The contiguous confirmed-fetched interval, or null when nothing is. */
  covered: { startMs: number; endMs: number } | null;
  /** Resident stand-in while the active level can't cover the viewport
   *  (rung just crossed, or a flick outran the cache): another level's buckets,
   *  BY REFERENCE — presentation only, never merged into the active level.
   *  The chart keeps valid alternate-resolution history full-strength and
   *  veils only what NEITHER covers; `covered` still means freshly fetched. */
  fallback: { buckets: HistoryBucket[]; bucketMs: number | null; covered: { startMs: number; endMs: number } } | null;
  /** Bumped on every data change — the chart's per-frame dirty check. */
  version: number;
}

/** Why the shared viewport moved. Explicit range selections must not be
 * mistaken for wheel/pointer zooms: they seed a one-shot finer-rung warmup,
 * while gestures derive their direction from the previous viewport. */
export type ViewportChangeCause = 'gesture' | 'selection';

interface LevelState {
  bucketSec: number | null; // null = raw
  buckets: HistoryBucket[]; // sorted by tsMs
  covered: { startMs: number; endMs: number } | null;
  /** In-flight ranges. `prefetch` marks a speculative adjacent-rung flight —
   *  it never blocks a visible-need launch (which supersedes it at the worker
   *  via the shared flightKey), and its failure is silent (no chip, no retry). */
  pending: Array<{ startMs: number; endMs: number; prefetch?: boolean }>;
  lastUsed: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
}

/** Prefetch this many viewport-widths beyond EACH edge (1 ⇒ 3× resident). Safe
 *  for AGGREGATED levels — server-bucketed cost is roughly constant regardless
 *  of window size (bounded bucket count either way). */
const PREFETCH_MARGIN = 1;
/** Raw-mode margin is much smaller: unlike aggregated buckets, a raw fetch's
 *  cost scales with however many samples actually occurred in the window —
 *  unbounded for a fast-changing signal — so 3× the viewport risked timing out
 *  on exactly the signal (FastChanging.Sawtooth_02) this tab defaults to. */
const RAW_PREFETCH_MARGIN = 0.25;
/** Extra viewport-widths RETAINED per side beyond the prefetch margin before
 *  trimming kicks in — memory-only hysteresis, no extra fetching. Without it
 *  every ensure() pass trimmed covered data to viewport ± margin, so panning
 *  forward and immediately back refetched ground discarded moments earlier
 *  (slow on a cold historian → veil). The rows were already paid for; keeping
 *  them one more span is a few thousand buckets per level at most. */
const RETAIN_EXTRA = 1;
/** Raw retention is smaller only to bound row count, mirroring the margin. */
const RAW_RETAIN_EXTRA = 0.5;
/** Zoom levels kept resident; older ones evict (snap-back stays warm). */
const MAX_LEVELS = 3;
/** Cadence of fetch-planning passes — a leading+trailing THROTTLE window, not
 *  a debounce. A gesture streams viewport commits at 60–120Hz (momentum
 *  included); one pass runs per window, so data keeps loading DURING the
 *  gesture (the old per-event debounce reset meant nothing fetched until the
 *  user fully stopped). Caps a lane at ~7 fetches/s while keeping first paint
 *  instant; the last armed pass doubles as the post-gesture settle. */
const ENSURE_MIN_INTERVAL_MS = 150;
/** Mid-gesture fetch lookahead, in viewport-spans, extended AHEAD of the pan
 *  direction so landings stay in front of the pan instead of arriving for
 *  pixels already scrolled past. Deliberately < PREFETCH_MARGIN so a runway
 *  target always sits inside desiredRange (trim/merge invariants hold). */
const PAN_RUNWAY_FRAC = 0.75;
/** Raw-mode runway mirrors RAW_PREFETCH_MARGIN: raw fetch cost scales with
 *  however many samples occurred, so the lookahead must stay small. */
const RAW_PAN_RUNWAY_FRAC = 0.25;
/** A viewport commit within this window means a gesture is in progress (the
 *  runway applies); it has expired by the time the trailing settle pass runs,
 *  so the at-rest ±margin build is never runway-biased. */
const GESTURE_ACTIVE_MS = 250;
const RETRY_MS = 2000;
/** Self-heal watchdog cadence — see ensure(). */
const HEAL_MS = 1000;
/** Floor between ADJACENT-RUNG prefetches (see maybePrefetchAdjacent) — one
 *  speculative flight per two ensure windows per lane at most, so a sustained
 *  zoom adds ≤ ~3 flights/s on top of the ~7/s planning cap. */
const PREFETCH_ADJACENT_MIN_INTERVAL_MS = 300;
/** Never speculatively prefetch RAW samples for a viewport wider than this:
 *  raw cost scales with sample count, not pixels. The 2s→raw crossing happens
 *  well under this span at any plausible plot width, so the crossing is still
 *  covered while a pathological wide-span raw prefetch is impossible. */
const RAW_PREFETCH_MAX_SPAN_MS = 900_000;
/** Speculative data must fit inside a fixed per-lane point budget. The active
 * visible level wins over this soft ceiling (correctness first), but adjacent
 * levels are evicted/skipped before lookahead can grow the lane beyond it. */
const CACHE_POINT_BUDGET = 220_000;
/** Raw lookahead is allowed only when the active aggregated buckets prove the
 * viewport contains no more than this many underlying samples. Unlike a time
 * cap, this also protects dense/high-frequency signals. */
const RAW_PREFETCH_POINT_BUDGET = 50_000;

/**
 * Splice a landed fetch into a level's resident buckets: every resident bucket
 * inside [startMs, endMs] is REPLACED by the landing (aligned grids ⇒ the same
 * bucket re-fetched, never a near-duplicate), everything outside is kept, and
 * the result stays tsMs-sorted. Resident buckets are sorted by invariant; the
 * landing is sorted by the worker EXCEPT on the raw path, where the historian's
 * limit-cut reply can arrive out of order — so it is checked, and sorted only
 * then. Both inputs sorted ⇒ one linear pass, where the old filter + push +
 * full sort ran O(n log n) on every landing, prefetch margins included.
 *
 * No push(...spread): a paged raw fetch can carry 100k+ rows, past the
 * engines' call-argument cap — the spread threw RangeError, which the fetch
 * catch treated as a failure and retried the identical merge every 2s forever.
 */
function mergeLanding(resident: HistoryBucket[], landing: HistoryBucket[], startMs: number, endMs: number): HistoryBucket[] {
  let sorted = true;
  for (let i = 1; i < landing.length; i++) {
    if (landing[i].tsMs < landing[i - 1].tsMs) {
      sorted = false;
      break;
    }
  }
  if (!sorted) landing = landing.slice().sort((a, b) => a.tsMs - b.tsMs);
  const merged: HistoryBucket[] = [];
  let i = 0;
  let j = 0;
  while (i < resident.length || j < landing.length) {
    if (i < resident.length && resident[i].tsMs >= startMs && resident[i].tsMs <= endMs) {
      i++; // inside the refetched span: superseded by the landing
      continue;
    }
    if (j < landing.length && (landing[j].tsMs < startMs || landing[j].tsMs > endMs)) {
      j++; // outside the range this flight was issued for: not ours to keep
      continue;
    }
    if (j >= landing.length || (i < resident.length && resident[i].tsMs <= landing[j].tsMs)) merged.push(resident[i++]);
    else merged.push(landing[j++]);
  }
  return merged;
}

export class HistoryViewportController {
  readonly viewport: TimelineViewport;
  /** Fires after any data/viewport/error change — the section re-renders. */
  onUpdate?: () => void;
  /** True while the LAST fetch attempt for the active level failed. */
  fetchFailed = false;

  private readonly data: TimelineData = { buckets: [], bucketMs: null, covered: null, fallback: null, version: 0 };
  private readonly levels = new Map<string, LevelState>();
  private plotWidth = 800; // placeholder until the chart's first plot-resize
  private plotWidthKnown = false; // no fetch until the REAL width has arrived
  private passTimer: ReturnType<typeof setTimeout> | null = null;
  /** Self-heal watchdog (see ensure()) — armed while the viewport is blind. */
  private healTimer: ReturnType<typeof setTimeout> | null = null;
  private lastEnsureAt = 0;
  private disposed = false;
  /** Pan direction of the gesture in progress (0 = none/zoom) — drives the
   *  mid-gesture runway so fetches land AHEAD of the pan. */
  private panDir: -1 | 0 | 1 = 0;
  /** Zoom direction of the gesture in progress (1 = span shrinking / finer
   *  rung next, −1 = growing / coarser next, 0 = none/pan) — drives the
   *  adjacent-rung prefetch so a rung crossing finds data already resident. */
  private zoomDir: -1 | 0 | 1 = 0;
  /** Explicit range selection primes the next-finer rung ahead of ordinary
   * same-level margins. Consumed after one bounded attempt. */
  private primeFiner = false;
  /** Stamp of the last adjacent-rung prefetch launch (rate limit). */
  private lastPrefetchAt = 0;
  /** Stamp of the last viewport commit — a commit within GESTURE_ACTIVE_MS
   *  means a gesture is live. */
  private lastViewportChangeAt = 0;

  /**
   * The FROZEN "now" — the tab-open instant. This view is retrospective by
   * decree ("nothing moves that the user did not move"): every right-edge
   * clamp uses this snapshot, never live Date.now(), so neither gestures nor
   * the retry loop can drift the window (or its prefetch coverage) into the
   * live present. Advanced ONLY by reanchor() — an explicit range selection.
   *
   * Injectable so every lane of a multi-symbol view shares ONE clock: a lane
   * added later must clamp to the same instant as the others, or their
   * viewports drift apart on the first zoom-out against the right edge.
   */
  private anchorNowMs: number;

  constructor(
    private readonly backend: BackendClient,
    private readonly gatewayId: string,
    private readonly controllerId: string,
    private readonly symbolPath: string,
    initialViewport: TimelineViewport,
    anchorMs: number = Date.now(),
  ) {
    this.viewport = { ...initialViewport };
    this.anchorNowMs = anchorMs;
  }

  /** The frozen clock — the newest instant this view may show. */
  get anchorMs(): number {
    return this.anchorNowMs;
  }

  /** Advance the frozen clock to the present. Only for deliberate range
   *  selections (preset pill, custom-range apply) — never gestures. Callers
   *  owning several controllers pass ONE stamp to all of them, so every lane
   *  advances to the same instant. */
  reanchor(nowMs: number = Date.now()): void {
    this.anchorNowMs = nowMs;
    this.panDir = 0; // an explicit range selection is not a pan
    this.zoomDir = 0; // ...nor a zoom
  }

  dispose(): void {
    this.disposed = true;
    if (this.passTimer) clearTimeout(this.passTimer);
    if (this.healTimer) clearTimeout(this.healTimer);
    for (const l of this.levels.values()) if (l.retryTimer) clearTimeout(l.retryTimer);
    this.levels.clear();
  }

  /** The active level's render snapshot (stable object; `version` bumps). */
  get timelineData(): TimelineData {
    return this.data;
  }

  /** The chart reports its plot width here (ladder input) — refetch if it
   *  changes the resolution or uncovers margin. NOTHING fetches before the
   *  first real report: `plotWidth` starts as a guess, and fetching against
   *  it picked a bucket rung the real width immediately invalidated — a whole
   *  wasted query round on every mount. The flag (not a value comparison)
   *  gates it, so a real width that happens to equal the default still fires. */
  setPlotWidth(px: number): void {
    if (px <= 0) return;
    if (this.plotWidthKnown && px === this.plotWidth) return;
    this.plotWidth = px;
    this.plotWidthKnown = true;
    this.schedule();
  }

  /** Commit a viewport (gesture or preset). Optimistic — the window moves NOW;
   *  data for newly-exposed range follows (throttled). Clamped: nothing past
   *  the frozen anchor, no sub-2s/super-year spans. */
  setViewport(startMs: number, endMs: number, cause: ViewportChangeCause = 'gesture'): void {
    let span = Math.min(MAX_VIEWPORT_SPAN_MS, Math.max(MIN_VIEWPORT_SPAN_MS, endMs - startMs));
    let e = Math.min(endMs, this.anchorNowMs);
    let s = e - span;
    // Classify the commit against the previous viewport BEFORE overwriting it:
    // a span change is a zoom — it sets the zoom direction the adjacent-rung
    // prefetch anticipates toward (and clears the pan runway); a pure
    // translation sets the pan direction the mid-gesture runway extends
    // toward. Zero delta keeps the previous direction (momentum events can
    // repeat a frame).
    const prevSpan = this.viewport.endMs - this.viewport.startMs;
    if (cause === 'selection') {
      // A picker/preset jump is not a gesture direction. Prime the interaction
      // users overwhelmingly make next (zoom in to inspect the chosen range),
      // but don't let the jump activate pan runway or zoom-out prediction.
      this.panDir = 0;
      this.zoomDir = 0;
      this.primeFiner = true;
      this.lastViewportChangeAt = 0;
    } else {
      // Wheel/trackpad zoom can arrive in sub-5% increments. Treat any span
      // change above float-noise as zoom; otherwise a slow zoom is mislabeled
      // as a pan and never primes the next resolution.
      const spanEpsilon = Math.max(1, prevSpan * 1e-6);
      if (Math.abs(span - prevSpan) > spanEpsilon) {
        this.panDir = 0;
        this.zoomDir = span < prevSpan ? 1 : -1;
        if (this.zoomDir < 0) this.primeFiner = false;
      } else if (s !== this.viewport.startMs) {
        this.panDir = s > this.viewport.startMs ? 1 : -1;
        this.zoomDir = 0;
        this.primeFiner = false;
      }
      this.lastViewportChangeAt = Date.now();
    }
    this.viewport.startMs = s;
    this.viewport.endMs = e;
    this.syncActiveLevel();
    this.schedule();
    this.onUpdate?.();
  }

  /** First load / explicit refresh. */
  refresh(): void {
    this.syncActiveLevel();
    this.schedule();
  }

  // ── internals ──────────────────────────────────────────────────────────────

  private levelKey(bucketSec: number | null): string {
    return bucketSec === null ? 'raw' : String(bucketSec);
  }

  private activeLevel(): LevelState {
    return this.levelFor(pickBucketSeconds(this.viewport, this.plotWidth));
  }

  /** Get-or-mint the level for a rung, with the bounded-cache eviction — used
   *  by the active path and by the adjacent-rung prefetch. */
  private levelFor(bucketSec: number | null): LevelState {
    const key = this.levelKey(bucketSec);
    let level = this.levels.get(key);
    if (!level) {
      level = { bucketSec, buckets: [], covered: null, pending: [], lastUsed: Date.now(), retryTimer: null };
      this.levels.set(key, level);
      // Bounded: evict beyond the cap — EMPTY levels first. A continuous zoom
      // mints one empty level per crossed ladder rung, and plain oldest-first
      // would evict the single level that still HAS data — the only possible
      // fallback source while the fresh rung loads, leaving the lane a bare
      // veil for the whole round-trip. Oldest-overall only when every
      // candidate holds data.
      if (this.levels.size > MAX_LEVELS) {
        let oldestKey = '';
        let oldest = Infinity;
        let oldestEmptyKey = '';
        let oldestEmpty = Infinity;
        for (const [k, l] of this.levels) {
          if (k === key) continue;
          if (l.lastUsed < oldest) {
            oldest = l.lastUsed;
            oldestKey = k;
          }
          if (l.buckets.length === 0 && l.lastUsed < oldestEmpty) {
            oldestEmpty = l.lastUsed;
            oldestEmptyKey = k;
          }
        }
        const evictKey = oldestEmptyKey || oldestKey;
        if (evictKey) {
          const evicted = this.levels.get(evictKey);
          if (evicted?.retryTimer) clearTimeout(evicted.retryTimer);
          this.levels.delete(evictKey);
        }
      }
    }
    level.lastUsed = Date.now();
    return level;
  }
  private evictLevel(key: string): void {
    const level = this.levels.get(key);
    if (!level) return;
    if (level.retryTimer) clearTimeout(level.retryTimer);
    this.levels.delete(key);
  }

  private cachedPointCount(): number {
    let n = 0;
    for (const level of this.levels.values()) n += level.buckets.length;
    return n;
  }

  /** Make room for speculative points without evicting the active level or the
   * target being warmed. Returns false when visible data alone consumes the
   * budget, in which case lookahead simply stays off. */
  private reservePrefetchPoints(active: LevelState, target: LevelState, points: number): boolean {
    if (points <= 0) return true;
    let projected = this.cachedPointCount() + points;
    if (projected <= CACHE_POINT_BUDGET) return true;
    const candidates = [...this.levels.entries()]
      .filter(([, level]) => level !== active && level !== target)
      .sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    for (const [key, level] of candidates) {
      projected -= level.buckets.length;
      this.evictLevel(key);
      if (projected <= CACHE_POINT_BUDGET) return true;
    }
    return false;
  }

  /** Estimate raw rows from the finest resident aggregated level that fully
   * covers the requested range. sampleCount is the exact underlying-row count
   * per bucket, so this is a density guard rather than a time-window guess. */
  private estimatedRawPoints(startMs: number, endMs: number): number | null {
    let best: LevelState | null = null;
    for (const level of this.levels.values()) {
      if (level.bucketSec === null || !level.covered) continue;
      if (level.covered.startMs > startMs || level.covered.endMs < endMs) continue;
      if (!best || level.bucketSec < best.bucketSec!) best = level;
    }
    if (!best) return null;
    const bucketMs = best.bucketSec! * 1000;
    let n = 0;
    for (const bucket of best.buckets) {
      if (bucket.tsMs >= endMs || bucket.tsMs + bucketMs <= startMs) continue;
      n += bucket.sampleCount;
      if (n > RAW_PREFETCH_POINT_BUDGET) return n;
    }
    return n;
  }

  /** Point the render snapshot at the active level's current contents. */
  private syncActiveLevel(): void {
    const level = this.activeLevel();
    this.data.buckets = level.buckets;
    this.data.bucketMs = level.bucketSec === null ? null : level.bucketSec * 1000;
    this.data.covered = level.covered;
    this.data.fallback = this.pickFallback(level);
    this.data.version++;
  }

  /** The resident stand-in for whatever part of the viewport the active level
   *  doesn't cover yet: the best OTHER resident level intersecting it —
   *  nearest-coarser rung preferred (its buckets span the widest range for the
   *  fewest points), nearest-finer otherwise, raw counting as finest. Null
   *  when the active level fully covers the viewport (the common at-rest
   *  case) or nothing resident intersects. */
  private pickFallback(active: LevelState): TimelineData['fallback'] {
    const vpStart = this.viewport.startMs;
    const vpEnd = this.viewport.endMs;
    if (active.covered && active.covered.startMs <= vpStart && active.covered.endMs >= vpEnd) return null;
    const activeSec = active.bucketSec ?? 0; // raw = finest
    // Rank: coarser-than-active by closeness first, then finer by closeness.
    const FINER_PENALTY = Number.MAX_SAFE_INTEGER / 2;
    let best: TimelineData['fallback'] = null;
    let bestRank = Infinity;
    for (const l of this.levels.values()) {
      if (l === active || !l.covered || l.buckets.length === 0) continue;
      if (l.covered.startMs >= vpEnd || l.covered.endMs <= vpStart) continue;
      const sec = l.bucketSec ?? 0;
      const rank = sec >= activeSec ? sec - activeSec : FINER_PENALTY + (activeSec - sec);
      if (rank < bestRank) {
        bestRank = rank;
        best = { buckets: l.buckets, bucketMs: l.bucketSec === null ? null : l.bucketSec * 1000, covered: l.covered };
      }
    }
    return best;
  }

  /** The range worth holding for the current viewport (viewport ± margin,
   *  snapped to this level's bucket grid so refetches align with the server's
   *  epoch-aligned buckets and merge cleanly). */
  private desiredRange(level: LevelState): { startMs: number; endMs: number } {
    const span = this.viewport.endMs - this.viewport.startMs;
    const margin = level.bucketSec === null ? RAW_PREFETCH_MARGIN : PREFETCH_MARGIN;
    const snapped = snapRangeToBuckets(
      this.viewport.startMs - span * margin,
      this.viewport.endMs + span * margin,
      level.bucketSec ?? 1,
    );
    // Clamp to the FROZEN anchor, not Date.now() — otherwise every ensure()
    // (gesture, resize, failure-retry) would creep coverage into the live
    // present and the tab would quietly turn into a live view. AFTER snapping:
    // the snap rounds the end UP to a bucket boundary, which would leak up to
    // one bucket past the anchor (post-anchor samples in the final bucket).
    return { startMs: snapped.startMs, endMs: Math.min(snapped.endMs, this.anchorNowMs) };
  }

  /** Fetch trigger — a leading+trailing THROTTLE, not a debounce. The first
   *  call in a window runs a planning pass immediately (first paint stays
   *  instant); later calls arm ONE timer for the remainder of the window and
   *  never re-arm it, so a continuous gesture ticks a pass every
   *  ENSURE_MIN_INTERVAL_MS instead of waiting for the gesture to end (the
   *  old per-event debounce reset meant nothing fetched until the user fully
   *  stopped, momentum included). The last armed timer fires ≤ one window
   *  after the final event — that IS the trailing settle pass. Still bounded:
   *  un-throttled per-event passes once flooded the historian's query
   *  semaphore until head-of-line queueing blew the deadlines. */
  private schedule(): void {
    if (this.disposed || !this.plotWidthKnown) return;
    const elapsed = Date.now() - this.lastEnsureAt;
    if (elapsed >= ENSURE_MIN_INTERVAL_MS) {
      if (this.passTimer) {
        clearTimeout(this.passTimer);
        this.passTimer = null;
      }
      this.ensure();
    } else if (!this.passTimer) {
      this.passTimer = setTimeout(() => {
        this.passTimer = null; // don't leave a dead handle looking armed
        this.ensure();
      }, ENSURE_MIN_INTERVAL_MS - elapsed);
    }
  }

  /** Every fetch-planning entry point funnels through here: run one planning
   *  pass, then arm the self-heal watchdog if the visible viewport is still
   *  not fully covered. The watchdog is defense-in-depth against terminal
   *  idle states (a pass that ends with nothing airborne, no timer, and an
   *  uncovered viewport — e.g. via a bug in a settlement path): any such
   *  state now costs a ~1s blip instead of a freeze that only the next user
   *  gesture breaks. It cannot spin (a tick with a legitimate flight in the
   *  air just defers and re-arms; once covered it stops re-arming) and
   *  cannot duplicate fetches (the in-flight policy and dedupe guards run
   *  on every pass). */
  private ensure(): void {
    if (this.healTimer) {
      clearTimeout(this.healTimer);
      this.healTimer = null;
    }
    try {
      this.ensurePass();
    } finally {
      this.armHeal();
    }
  }

  private armHeal(): void {
    if (this.disposed || !this.plotWidthKnown) return;
    const level = this.activeLevel();
    const covered =
      level.covered !== null &&
      level.covered.startMs <= this.viewport.startMs &&
      level.covered.endMs >= this.viewport.endMs;
    // A level in failure-backoff already has its retryTimer as the wake-up —
    // don't undercut the deliberate 2s backoff with a 1s heal.
    if (covered || level.retryTimer) return;
    this.healTimer = setTimeout(() => {
      this.healTimer = null;
      this.schedule();
    }, HEAL_MS);
  }

  /** Evict data the viewport has moved away from — the flat-memory half of
   *  "greedy about prefetching, strict about memory". EXCEPT when the
   *  viewport jumped clean off the loaded range: then the stale buckets stay
   *  (the chart keeps something on screen instead of a blank) and the next
   *  successful merge REPLACES them atomically — see the disjoint branch in
   *  fetch()'s success handler.
   *  Trim against a RETAIN range wider than desired (hysteresis): data stays
   *  resident one extra span per side, so pan-back redraws from cache instead
   *  of refetching. Disjointness is still judged against desired — the
   *  "jumped clean off" semantics (and the replace-on-landing that pairs
   *  with it) are unchanged. Returns true when covered is DISJOINT from
   *  desired. Shared by the active path and the adjacent-rung prefetch (a
   *  non-active level would otherwise never trim and grow unboundedly across
   *  repeated prefetches). */
  private trimToRetain(level: LevelState, desired: { startMs: number; endMs: number }): boolean {
    if (!level.covered) return false;
    const span = this.viewport.endMs - this.viewport.startMs;
    const extra = span * (level.bucketSec === null ? RAW_RETAIN_EXTRA : RETAIN_EXTRA);
    const retainSnap = snapRangeToBuckets(desired.startMs - extra, desired.endMs + extra, level.bucketSec ?? 1);
    const retainEnd = Math.min(retainSnap.endMs, this.anchorNowMs);
    if (Math.max(level.covered.startMs, desired.startMs) >= Math.min(level.covered.endMs, desired.endMs)) {
      return true;
    }
    const keepStart = Math.max(level.covered.startMs, retainSnap.startMs);
    const keepEnd = Math.min(level.covered.endMs, retainEnd);
    if (keepStart > level.covered.startMs || keepEnd < level.covered.endMs) {
      level.covered = { startMs: keepStart, endMs: keepEnd };
      // SENTINELS: keep the single nearest bucket OUTSIDE each keep edge.
      // At deep zoom the retain window shrinks to almost nothing in absolute
      // time, and evicting the sample just left of the viewport visibly
      // disconnected the polyline at the edge (it no longer entered from
      // off-screen). One point per side, re-derived on every trim so they
      // can't accumulate; they stay OUTSIDE `covered` — resident for drawing,
      // never "confirmed fetched" for the loading veil.
      let leftSentinel = -1;
      let rightSentinel = -1;
      for (let i = 0; i < level.buckets.length; i++) {
        const ts = level.buckets[i].tsMs;
        if (ts < keepStart) leftSentinel = i;
        else if (ts > keepEnd) {
          rightSentinel = i;
          break;
        }
      }
      level.buckets = level.buckets.filter(
        (b, i) => (b.tsMs >= keepStart && b.tsMs <= keepEnd) || i === leftSentinel || i === rightSentinel,
      );
    }
    return false;
  }

  private ensurePass(): void {
    if (this.disposed) return;
    this.lastEnsureAt = Date.now();
    const level = this.activeLevel();
    const desired = this.desiredRange(level);
    const gestureActive = Date.now() - this.lastViewportChangeAt < GESTURE_ACTIVE_MS;
    const disjoint = this.trimToRetain(level, desired);

    // What's missing: the whole range, or a delta off either covered edge.
    // A disjoint covered range contributes nothing — its data is stale
    // hold-over, not a fetch-frontier to extend from.
    const wanted: Array<{ startMs: number; endMs: number }> = [];
    if (!level.covered || disjoint) {
      wanted.push(desired);
    } else {
      if (desired.startMs < level.covered.startMs) wanted.push({ startMs: desired.startMs, endMs: level.covered.startMs });
      if (desired.endMs > level.covered.endMs) wanted.push({ startMs: level.covered.endMs, endMs: desired.endMs });
    }

    // VISIBLE-FIRST: while any of the viewport itself is blind, fetch only the
    // visible slice of each missing delta — first paint must never wait on the
    // prefetch margin (on a cold historian the margin tripled the scanned file
    // count before anything could show). Margins follow automatically: every
    // successful merge re-schedules, and by then the viewport is covered so
    // the next pass emits the remaining margin deltas.
    //
    // Mid-gesture the blind deltas coalesce into ONE fetch (union range):
    // per-bucket server cost is the same either way (no aggregation pushdown —
    // the parquet scan spans the whole window regardless), it keeps `covered`
    // trivially contiguous, and one flight per lane is what the historian's
    // query semaphore is sized for (3 lanes × 3 agg kinds = 9 — see
    // services/historian/internal/query/query.go's queryConcurrency).
    const vp = snapRangeToBuckets(this.viewport.startMs, this.viewport.endMs, level.bucketSec ?? 1);
    const vpEnd = Math.min(vp.endMs, this.anchorNowMs);
    const viewportCovered =
      level.covered !== null && !disjoint && level.covered.startMs <= vp.startMs && level.covered.endMs >= vpEnd;
    let targets: Array<{ startMs: number; endMs: number }>;
    if (viewportCovered) {
      targets = wanted;
    } else {
      const vis = wanted
        .map((w) => ({ startMs: Math.max(w.startMs, vp.startMs), endMs: Math.min(w.endMs, vpEnd) }))
        .filter((w) => w.endMs - w.startMs >= 1);
      if (vis.length === 0) {
        targets = [];
      } else {
        let lo = Math.min(...vis.map((v) => v.startMs));
        let hi = Math.max(...vis.map((v) => v.endMs));
        // RUNWAY: while a gesture is live, extend the target AHEAD of the pan
        // so the landing covers ground about to be revealed — a bare visible
        // sliver is stale by the time the round-trip completes, which is why
        // data used to appear only after the gesture ended. Clamped inside
        // `desired` (runway < margin), so trim/merge invariants are untouched;
        // the landing overlaps-or-abuts `covered` and unions cleanly.
        if (gestureActive && this.panDir !== 0) {
          const runway =
            (this.viewport.endMs - this.viewport.startMs) *
            (level.bucketSec === null ? RAW_PAN_RUNWAY_FRAC : PAN_RUNWAY_FRAC);
          if (this.panDir < 0) lo = Math.max(desired.startMs, lo - runway);
          else hi = Math.min(desired.endMs, hi + runway);
          // A stale direction right after a reversal could stretch the target
          // into ground already resident — clamp back to the covered edge.
          // Only with a single blind sliver: when BOTH edges are blind the
          // union legitimately spans `covered` and must not be cut.
          if (level.covered && !disjoint && vis.length === 1) {
            if (lo >= level.covered.startMs && lo < level.covered.endMs) lo = level.covered.endMs;
            if (hi > level.covered.startMs && hi <= level.covered.endMs) hi = level.covered.startMs;
          }
          // Re-snap the extended edges to the bucket grid (server cache hits),
          // then re-clamp: the snap rounds the end UP past the frozen anchor.
          const snapped = snapRangeToBuckets(lo, hi, level.bucketSec ?? 1);
          lo = snapped.startMs;
          hi = Math.min(snapped.endMs, this.anchorNowMs);
        }
        targets = hi - lo >= 1 ? [{ startMs: lo, endMs: hi }] : [];
      }
    }

    this.syncActiveLevel();

    // Lane-wide in-flight policy (every level's fetch shares one flightKey):
    //  - a flight OVERLAPPING the viewport is still useful — DEFER to it
    //    rather than abort it. With fetch latency above the gesture cadence,
    //    supersede-always meant no fetch ever completed until the user paused
    //    for a full round-trip (the screen stayed dark the whole gesture).
    //    Every settlement path re-runs schedule(), so the deferred pass
    //    resumes the moment the flight lands (or fails, or the 12s worker
    //    timeout fires) and fetches against the then-current viewport.
    //  - a pure MARGIN fetch never launches while anything is in flight —
    //    otherwise the left/right margin fetches alternately abort each other.
    //  - only a flight that is entirely OFF-SCREEN is superseded, by issuing
    //    the new visible fetch under the same flightKey: visible beats
    //    invisible, the one supersede that is actually worth an abort.
    //  - a PREFETCH flight defers a visible-need launch only when it is about
    //    to ANSWER it (same level, range covering the whole viewport — the
    //    just-crossed-a-rung case, where aborting it would throw away an
    //    almost-landed superset). Any other prefetch never blocks: the
    //    visible fetch launches under the shared flightKey and the worker
    //    aborts the prefetch.
    const vpStart = this.viewport.startMs;
    const vpEnd2 = this.viewport.endMs;
    let pendingAny = false;
    let pendingOverlapsViewport = false;
    for (const l of this.levels.values()) {
      for (const p of l.pending) {
        pendingAny = true;
        if (p.prefetch) {
          if (l === level && p.startMs <= vpStart && p.endMs >= vpEnd2) pendingOverlapsViewport = true;
        } else if (p.startMs < vpEnd2 && p.endMs > vpStart) pendingOverlapsViewport = true;
      }
    }
    if (pendingOverlapsViewport) return;

    let launched = false;
    // A range selection first primes the next-finer rung; during a zoom gesture
    // in either direction, the predicted adjacent rung beats same-level margins
    // that the crossing is about to strand. Selection bypasses only the short
    // adjacent-flight cadence floor: it is a discrete user action and the
    // lane-wide one-flight rule still bounds concurrency.
    if (!pendingAny && viewportCovered && this.primeFiner) {
      this.primeFiner = false;
      launched = this.maybePrefetchAdjacent(level, 1, true);
    } else if (!pendingAny && viewportCovered && gestureActive && this.zoomDir !== 0) {
      launched = this.maybePrefetchAdjacent(level, this.zoomDir);
    }
    if (!launched) {
      for (const w of targets) {
        if (w.endMs - w.startMs < 1) continue;
        // Skip ranges already in flight (a settled fetch removes its entry).
        if (level.pending.some((p) => p.startMs <= w.startMs && p.endMs >= w.endMs)) continue;
        // Margin-only targets wait for the air to clear (policy above).
        if (pendingAny && !(w.startMs < vpEnd2 && w.endMs > vpStart)) continue;
        this.fetch(level, w.startMs, w.endMs);
        // ONE flight per lane per pass. Each settlement re-schedules, so any
        // remaining delta follows sequentially.
        launched = true;
        break;
      }
    }
    // Lowest priority: with the viewport AND margins resolved and nothing
    // airborne, speculatively warm the adjacent rung so the next crossing
    // finds data resident before the refinement crossing.
    if (!launched && !pendingAny && viewportCovered) this.maybePrefetchAdjacent(level, 1);
  }

  /**
   * Adjacent-rung prefetch — the refinement lookahead. A zoom crossing a
   * ladder rung otherwise relies on the resident alternate resolution for a
   * full round trip; this warms the rung the current gesture (or the next zoom
   * step, when idle) is headed for. Runs only when the visible viewport is
   * covered and nothing is airborne. Directional/selection lookahead outranks
   * margins; idle lookahead follows them. It is rate-limited and supersedable
   * by any real need, so it never delays visible data or raises the
   * one-flight-per-lane concurrency. Returns true when a flight launched.
   */
  private maybePrefetchAdjacent(active: LevelState, direction: -1 | 1, bypassRateLimit = false): boolean {
    const nowMs = Date.now();
    if (!bypassRateLimit && nowMs - this.lastPrefetchAt < PREFETCH_ADJACENT_MIN_INTERVAL_MS) return false;
    const zoomOut = direction === -1;
    const predicted = zoomOut ? nextCoarserRung(active.bucketSec) : nextFinerRung(active.bucketSec);
    if (predicted === undefined) return false;
    const span = this.viewport.endMs - this.viewport.startMs;
    if (predicted === null && span > RAW_PREFETCH_MAX_SPAN_MS) return false;
    // Zoom-in / idle: the current viewport EXACTLY — every post-crossing
    // viewport is a sub-range of it, and at ~px/2 buckets per rung-ratio the
    // cost matches one ordinary margin fetch. Zoom-out: the viewport expanded
    // by the rung ratio (the clamped gesture math, reused), so the coarser
    // range that will fill the grown viewport costs a ~constant bucket count.
    let range: TimelineViewport = this.viewport;
    if (zoomOut && predicted !== null && active.bucketSec !== null) {
      range = zoomViewport(this.viewport, active.bucketSec / predicted, 0.5, this.anchorNowMs);
    }
    const snapped = snapRangeToBuckets(range.startMs, range.endMs, predicted ?? 1);
    let lo = snapped.startMs;
    let hi = Math.min(snapped.endMs, this.anchorNowMs); // frozen-clock clamp
    let pointEstimate: number;
    if (predicted === null) {
      const rawEstimate = this.estimatedRawPoints(lo, hi);
      // Gate raw BEFORE minting its level: a rejected dense prefetch must not
      // evict a useful resident rung merely by asking whether it is affordable.
      if (rawEstimate === null || rawEstimate > RAW_PREFETCH_POINT_BUDGET) return false;
      pointEstimate = rawEstimate;
    } else {
      pointEstimate = Math.ceil((hi - lo) / (predicted * 1000)) + 1;
    }
    const level = this.levelFor(predicted);
    const disjoint = this.trimToRetain(level, { startMs: lo, endMs: hi });
    // Fetch only the missing delta off a covered edge. Covered strictly
    // inside (or disjoint) falls through to the whole range — one contiguous
    // flight; the merge dedupes and a disjoint landing replaces (the fetch
    // contiguity guard).
    if (level.covered && !disjoint) {
      const c = level.covered;
      if (c.startMs <= lo && c.endMs >= hi) return false; // already warm
      if (c.startMs <= lo && c.endMs > lo) lo = c.endMs;
      else if (c.endMs >= hi && c.startMs < hi) hi = c.startMs;
    }
    if (hi - lo < 1) return false;
    if (predicted !== null) pointEstimate = Math.ceil((hi - lo) / (predicted * 1000)) + 1;
    if (!this.reservePrefetchPoints(active, level, pointEstimate)) return false;
    if (level.pending.some((p) => p.startMs <= lo && p.endMs >= hi)) return false;
    this.lastPrefetchAt = nowMs;
    this.fetch(level, lo, hi, true);
    return true;
  }

  private fetch(level: LevelState, startMs: number, endMs: number, isPrefetch = false): void {
    const pendingEntry = { startMs, endMs, prefetch: isPrefetch };
    level.pending.push(pendingEntry);
    // Settlement removes the pending entry FIRST, before either handler does
    // anything else. The success path synchronously re-runs ensure() (via
    // schedule's immediate branch), and ensure's in-flight policy reads
    // `pending`: with the removal in a .finally — one microtask AFTER .then —
    // that pass saw the very flight that was settling, deferred to it, and
    // the lane froze until the next user gesture (nothing was left airborne
    // to re-schedule). Idempotent, so the .finally backstop stays harmless.
    const settle = (): void => {
      const i = level.pending.indexOf(pendingEntry);
      if (i >= 0) level.pending.splice(i, 1);
    };
    this.backend.api
      .historyQuery(
        this.gatewayId,
        this.controllerId,
        this.symbolPath,
        startMs,
        endMs,
        level.bucketSec,
        // One flight per LANE, latest-wins: a newer fetch (any level) aborts
        // this one at the worker; the abort rejects with HistoryQuerySuperseded
        // which the catch below treats as "replaced", never as a failure.
        `${this.gatewayId}/${this.controllerId}/${this.symbolPath}`,
      )
      .then((buckets) => {
        settle();
        if (this.disposed) return;
        // IDENTITY check, not key presence: the rung may have been evicted
        // and RE-MINTED while this fetch flew — merging into the captured
        // orphan object would silently discard the data (the map's live
        // level is what renders). Either way there's nowhere useful to
        // merge, but a deferred ensure() pass may be WAITING on this very
        // settlement, so always re-schedule or the lane sits idle until the
        // next user gesture.
        if (this.levels.get(this.levelKey(level.bucketSec)) !== level) {
          this.schedule();
          return;
        }
        this.fetchFailed = false;
        // Contiguity guard: union `covered` with the landing range ONLY when
        // they overlap or abut (≤1 bucket slack for grid snapping). A DISJOINT
        // landing — the viewport jumped and stale hold-over buckets were kept
        // for display — must REPLACE, not union: unioning would mark the
        // never-fetched middle as "confirmed fetched", rendering it as a
        // confident gap instead of loading.
        const slackMs = (level.bucketSec ?? 1) * 1000;
        const contiguous =
          !level.covered || (startMs <= level.covered.endMs + slackMs && endMs >= level.covered.startMs - slackMs);
        // Merge: drop any resident buckets inside the refetched span (aligned
        // grids ⇒ replacements, not near-duplicates), then splice the landing
        // in. On a disjoint landing the stale hold-over is dropped wholesale.
        level.buckets = mergeLanding(contiguous ? level.buckets : [], buckets, startMs, endMs);
        level.covered =
          contiguous && level.covered
            ? { startMs: Math.min(level.covered.startMs, startMs), endMs: Math.max(level.covered.endMs, endMs) }
            : { startMs, endMs };
        // Unconditional (not just when this level is active): a merge into a
        // non-active level can change the FALLBACK the active snapshot points
        // at, and the canvas dirty-check only repaints on a version bump.
        this.syncActiveLevel();
        this.onUpdate?.();
        // Next phase: with the visible slice now covered, this pass emits the
        // margin deltas that visible-first held back (no-op once all covered).
        this.schedule();
      })
      .catch((err) => {
        settle();
        if (this.disposed) return;
        // Superseded ≠ failed: a newer fetch for this lane replaced this one
        // (latest-wins at the worker). The replacement IS the retry — no error
        // log, no failure chip, no retry timer. Still re-schedule: with our
        // own replacement airborne the pass just defers to it (no extra
        // fetch), and it makes this settlement path self-sufficient should a
        // supersede ever arrive from anywhere else.
        if ((err as Error | null)?.name === 'HistoryQuerySuperseded') {
          this.schedule();
          return;
        }
        // Failure ≠ empty: leave the range UNCOVERED (still renders as loading,
        // never as a false gap) and retry once things have settled.
        //
        // [HistoryTimeline] tag: the ONLY place in the app that logs it, so the
        // browser console's filter box isolates every failure that produced the
        // historyLoadFailed chip. err is a real Error here — Comlink 4.4's
        // built-in error transfer preserves .message/.name/.stack for a thrown
        // Error across the worker boundary — so err.message carries the actual
        // ConnectError reason (aborted / timed out / invalid argument / etc.),
        // not just "it failed".
        console.error('[HistoryTimeline] historyQuery failed', {
          gatewayId: this.gatewayId,
          controllerId: this.controllerId,
          symbol: this.symbolPath,
          bucketSec: level.bucketSec ?? 'raw',
          startMs,
          endMs,
          err,
        });
        // The failure chip is for failures the user can SEE: only a fetch that
        // overlaps the current viewport sets it. A margin-prefetch failure is
        // invisible (that range renders nothing the user is looking at) and
        // just retries silently below. A failed ADJACENT-RUNG prefetch is
        // fully silent — no chip even when it overlaps the viewport (the user
        // sees nothing missing: the active level still covers it) and no
        // retry timer (a 2s loop re-attempting a speculative fetch is exactly
        // the storm prefetch must never cause; the next gesture, settle pass,
        // or — if a crossing made the range a real need — the 1s watchdog
        // retries it through the normal path).
        if (!isPrefetch) {
          if (startMs < this.viewport.endMs && endMs > this.viewport.startMs) this.fetchFailed = true;
          if (!level.retryTimer) {
            level.retryTimer = setTimeout(() => {
              level.retryTimer = null;
              this.schedule();
            }, RETRY_MS);
          }
        }
        this.onUpdate?.();
      })
      .finally(settle); // idempotent backstop — both handlers settle first
  }

}
